import {
  isValidRegex,
  MAX_TEXT_FILTER_CONDITIONS,
  TEXT_FILTER_OPERATORS,
  type TextFilterCondition,
  type TextFilterOperator,
} from '@cmoh/shared'
import { Plus, X } from 'lucide-react'
import { useMemo } from 'react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { useTranslation } from '@/hooks/use-translation'
import type { TextAdvancedFilterProps } from '../../types/data-table.types'

const EMPTY_CONDITION: TextFilterCondition = { operator: 'contains', value: '' }

function isRegexOperator(op: TextFilterOperator): boolean {
  return op === 'regex' || op === 'notRegex'
}

/**
 * Advanced text filter with an operator dropdown (contains / notContains
 * / regex / notRegex / equals / notEquals) per condition. Up to
 * `MAX_TEXT_FILTER_CONDITIONS` conditions per column — they combine with
 * AND semantics. Invalid regex values surface inline; the filter only
 * commits non-empty conditions upstream.
 */
export function TextAdvancedFilter({
  columnId,
  columnTitle,
  value,
  onChange,
}: TextAdvancedFilterProps) {
  const t = useTranslation()

  const conditions = useMemo<TextFilterCondition[]>(() => {
    if (!value || value.length === 0) return [EMPTY_CONDITION]
    return value
  }, [value])

  const commit = (next: TextFilterCondition[]) => {
    const cleaned = next.filter((c) => c.value.trim().length > 0)
    onChange(cleaned.length === 0 ? undefined : cleaned)
  }

  const handleOperatorChange = (index: number, operator: TextFilterOperator) => {
    const next = conditions.map((c, i) => (i === index ? { ...c, operator } : c))
    commit(next)
  }

  const handleValueChange = (index: number, raw: string) => {
    const next = conditions.map((c, i) => (i === index ? { ...c, value: raw } : c))
    commit(next)
  }

  const handleAddCondition = () => {
    if (conditions.length >= MAX_TEXT_FILTER_CONDITIONS) return
    commit([...conditions, EMPTY_CONDITION])
  }

  const handleRemoveCondition = (index: number) => {
    commit(conditions.filter((_, i) => i !== index))
  }

  return (
    <div className="space-y-1.5">
      <label htmlFor={`filter-text-advanced-${columnId}-0`} className="text-xs font-medium">
        {columnTitle}
      </label>
      <div className="space-y-1.5">
        {conditions.map((condition, index) => {
          const hasValue = condition.value.trim().length > 0
          const regexInvalid =
            hasValue && isRegexOperator(condition.operator) && !isValidRegex(condition.value)
          const inputId = `filter-text-advanced-${columnId}-${index}`
          return (
            <div key={`${columnId}-${index}`} className="space-y-1">
              <div className="flex items-center gap-1.5">
                <Select
                  value={condition.operator}
                  onValueChange={(op) => handleOperatorChange(index, op as TextFilterOperator)}
                >
                  <SelectTrigger className="h-7 w-[124px] text-xs">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {TEXT_FILTER_OPERATORS.map((op) => (
                      <SelectItem key={op} value={op} className="text-xs">
                        {t(`dataTable.textOperator.${op}`)}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Input
                  id={inputId}
                  value={condition.value}
                  placeholder={t('dataTable.textAdvanced.placeholder')}
                  onChange={(e) => handleValueChange(index, e.target.value)}
                  className="h-7 flex-1 text-xs"
                  aria-invalid={regexInvalid}
                />
                {conditions.length > 1 && (
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={() => handleRemoveCondition(index)}
                    className="h-7 w-7 p-0"
                    aria-label={t('dataTable.textAdvanced.removeCondition')}
                  >
                    <X className="h-3 w-3" />
                  </Button>
                )}
              </div>
              {regexInvalid && (
                <p className="text-xs text-destructive">{t('dataTable.textAdvanced.invalidRegex')}</p>
              )}
            </div>
          )
        })}
      </div>
      {conditions.length < MAX_TEXT_FILTER_CONDITIONS && (
        <Button
          type="button"
          variant="ghost"
          size="sm"
          onClick={handleAddCondition}
          className="h-6 px-1.5 text-xs"
        >
          <Plus className="mr-1 h-3 w-3" />
          {t('dataTable.textAdvanced.addCondition')}
        </Button>
      )}
    </div>
  )
}
